import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { getKanbanSocketUrl } from 'utils/constants';
import type { KanbanCardData, KanbanColumn } from 'types/kanban';

/** Payloads from server (camelCase, match REST shapes) */
export interface KanbanSocketHandlers {
  onBoardCreated?: (board: { id: string; title: string }) => void;
  onBoardUpdated?: (board: { id: string; title: string }) => void;
  onBoardDeleted?: (payload: { boardId: string }) => void;
  onListCreated?: (list: {
    id: string;
    title: string;
    position: number;
    boardId: string;
  }) => void;
  onListUpdated?: (list: {
    id: string;
    title: string;
    position: number;
    boardId: string;
  }) => void;
  onListReordered?: (payload: { listIds: string[] }) => void;
  onListCopied?: (list: {
    id: string;
    title: string;
    position: number;
    boardId: string;
  }) => void;
  onListArchived?: (payload: {
    archivedListId: string;
    originalListId: string;
  }) => void;
  onListDeleted?: (payload: { listId: string }) => void;
  onCardCreated?: (card: KanbanCardData) => void;
  onCardUpdated?: (card: KanbanCardData) => void;
  onCardReordered?: (payload: { listId: string; cardIds: string[] }) => void;
  onCardComment?: (card: KanbanCardData) => void;
  onCardArchived?: (payload: {
    id: string;
    card: KanbanCardData;
    originalListId: string;
    originalListTitle?: string;
    originalPosition?: number;
    archivedAt?: string;
  }) => void;
  onCardDeleted?: (payload: { cardId: string }) => void;
  onLabelCreated?: (label: {
    id: string;
    name: string;
    color: string;
    boardId: string;
  }) => void;
  onLabelUpdated?: (label: {
    id: string;
    name: string;
    color: string;
    boardId: string;
  }) => void;
  onLabelDeleted?: (payload: { labelId: string }) => void;
  onMemberAdded?: (member: {
    id: string;
    name: string;
    boardId: string;
  }) => void;
  onMemberRemoved?: (payload: { memberId: string }) => void;
  onArchiveCardRestored?: (card: KanbanCardData) => void;
  onArchiveListRestored?: (list: {
    id: string;
    title: string;
    position: number;
    boardId: string;
  }) => void;
  onArchiveCardDeleted?: (payload: { archivedCardId: string }) => void;
  onArchiveListDeleted?: (payload: { archivedListId: string }) => void;
  onError?: (payload: { message: string }) => void;
}

/**
 * Subscribes to Kanban real-time events for a board. Connect only when boardId and companyId
 * are set; disconnect on unmount or when they change. Mutations are still done via REST.
 */
export function useKanbanSocket(
  boardId: string | null,
  companyId: string | null,
  handlers: KanbanSocketHandlers,
): void {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  useEffect(() => {
    const url = getKanbanSocketUrl();
    if (!url || !boardId || !companyId) {
      return;
    }

    let mounted = true;
    let socket: Socket | null = null;

    const connect = () => {
      if (!mounted) return;

      socket = io(url, {
        withCredentials: true,
      });

      socket.on('connect', () => {
        if (!mounted || !socket) return;
        socket.emit('subscribe', { boardId, companyId });
      });

      socket.on('error', (payload: { message: string }) => {
        handlersRef.current.onError?.(payload);
      });

      type H = KanbanSocketHandlers;
      const bind = <T>(event: string, cb: (payload: T) => void) => {
        socket?.on(event, (payload: unknown) => {
          if (mounted) cb(payload as T);
        });
      };

      bind<Parameters<NonNullable<H['onBoardCreated']>>[0]>(
        'board:created',
        (p) => handlersRef.current.onBoardCreated?.(p),
      );
      bind<Parameters<NonNullable<H['onBoardUpdated']>>[0]>(
        'board:updated',
        (p) => handlersRef.current.onBoardUpdated?.(p),
      );
      bind<Parameters<NonNullable<H['onBoardDeleted']>>[0]>(
        'board:deleted',
        (p) => handlersRef.current.onBoardDeleted?.(p),
      );
      bind<Parameters<NonNullable<H['onListCreated']>>[0]>(
        'list:created',
        (p) => handlersRef.current.onListCreated?.(p),
      );
      bind<Parameters<NonNullable<H['onListUpdated']>>[0]>(
        'list:updated',
        (p) => handlersRef.current.onListUpdated?.(p),
      );
      bind<Parameters<NonNullable<H['onListReordered']>>[0]>(
        'list:reordered',
        (p) => handlersRef.current.onListReordered?.(p),
      );
      bind<Parameters<NonNullable<H['onListCopied']>>[0]>('list:copied', (p) =>
        handlersRef.current.onListCopied?.(p),
      );
      bind<Parameters<NonNullable<H['onListArchived']>>[0]>(
        'list:archived',
        (p) => handlersRef.current.onListArchived?.(p),
      );
      bind<Parameters<NonNullable<H['onListDeleted']>>[0]>(
        'list:deleted',
        (p) => handlersRef.current.onListDeleted?.(p),
      );
      bind<Parameters<NonNullable<H['onCardCreated']>>[0]>(
        'card:created',
        (p) => handlersRef.current.onCardCreated?.(p),
      );
      bind<Parameters<NonNullable<H['onCardUpdated']>>[0]>(
        'card:updated',
        (p) => handlersRef.current.onCardUpdated?.(p),
      );
      bind<Parameters<NonNullable<H['onCardReordered']>>[0]>(
        'card:reordered',
        (p) => handlersRef.current.onCardReordered?.(p),
      );
      bind<Parameters<NonNullable<H['onCardComment']>>[0]>(
        'card:comment',
        (p) => handlersRef.current.onCardComment?.(p),
      );
      bind<Parameters<NonNullable<H['onCardArchived']>>[0]>(
        'card:archived',
        (p) => handlersRef.current.onCardArchived?.(p),
      );
      bind<Parameters<NonNullable<H['onCardDeleted']>>[0]>(
        'card:deleted',
        (p) => handlersRef.current.onCardDeleted?.(p),
      );
      bind<Parameters<NonNullable<H['onLabelCreated']>>[0]>(
        'label:created',
        (p) => handlersRef.current.onLabelCreated?.(p),
      );
      bind<Parameters<NonNullable<H['onLabelUpdated']>>[0]>(
        'label:updated',
        (p) => handlersRef.current.onLabelUpdated?.(p),
      );
      bind<Parameters<NonNullable<H['onLabelDeleted']>>[0]>(
        'label:deleted',
        (p) => handlersRef.current.onLabelDeleted?.(p),
      );
      bind<Parameters<NonNullable<H['onMemberAdded']>>[0]>(
        'member:added',
        (p) => handlersRef.current.onMemberAdded?.(p),
      );
      bind<Parameters<NonNullable<H['onMemberRemoved']>>[0]>(
        'member:removed',
        (p) => handlersRef.current.onMemberRemoved?.(p),
      );
      bind<Parameters<NonNullable<H['onArchiveCardRestored']>>[0]>(
        'archive:cardRestored',
        (p) => handlersRef.current.onArchiveCardRestored?.(p),
      );
      bind<Parameters<NonNullable<H['onArchiveListRestored']>>[0]>(
        'archive:listRestored',
        (p) => handlersRef.current.onArchiveListRestored?.(p),
      );
      bind<Parameters<NonNullable<H['onArchiveCardDeleted']>>[0]>(
        'archive:cardDeleted',
        (p) => handlersRef.current.onArchiveCardDeleted?.(p),
      );
      bind<Parameters<NonNullable<H['onArchiveListDeleted']>>[0]>(
        'archive:listDeleted',
        (p) => handlersRef.current.onArchiveListDeleted?.(p),
      );
    };

    connect();

    return () => {
      mounted = false;
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
      }
    };
  }, [boardId, companyId]);
}

/** Build a KanbanColumn from a list payload (e.g. list:created / list:updated) */
export function listPayloadToColumn(list: {
  id: string;
  title: string;
  position?: number;
  cardIds?: string[];
}): KanbanColumn {
  return {
    id: list.id,
    title: list.title,
    cardIds: list.cardIds ?? [],
  };
}
